import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Mail, Undo2 } from 'lucide-react';
import { clearComposerDraft, removeComposerDraft } from '../utils/composerDrafts';

const PendingSendContext = createContext(null);

export const usePendingSend = () => {
  const ctx = useContext(PendingSendContext);
  if (!ctx) {
    throw new Error('usePendingSend must be used within PendingSendProvider');
  }
  return ctx;
};

export const PendingSendProvider = ({ children }) => {
  const [pending, setPending] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [resumeNonce, setResumeNonce] = useState(0);
  const pendingRef = useRef(null);
  const tickRef = useRef(null);
  const expireRef = useRef(null);

  useEffect(() => {
    pendingRef.current = pending;
  }, [pending]);

  const clearTimers = useCallback(() => {
    if (tickRef.current) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (expireRef.current) {
      window.clearTimeout(expireRef.current);
      expireRef.current = null;
    }
  }, []);

  const finishPending = useCallback((next = null) => {
    clearTimers();
    pendingRef.current = next;
    setPending(next);
    setSecondsLeft(null);
  }, [clearTimers]);

  const cancelPending = useCallback(async ({ silent = false } = {}) => {
    const current = pendingRef.current;
    finishPending(null);
    if (!current?.scheduledId) return { cancelled: false };
    try {
      await axios.post(`/api/scheduled-emails/${current.scheduledId}/cancel`, {
        cancelToken: current.cancelToken || null,
      });
      if (!silent) toast('Versand abgebrochen', { icon: '↩️' });
      return { cancelled: true };
    } catch (_) {
      if (!silent) toast('Versand war bereits unterwegs oder abgelaufen');
      return { cancelled: false };
    }
  }, [finishPending]);

  const watchScheduledCompletion = useCallback(async (scheduledId, { draftId = null, mailbox = null } = {}) => {
    toast.loading('E-Mail wird versendet…', { id: 'scheduled-send' });
    let finalStatus = null;
    let lastError = null;
    for (let i = 0; i < 40; i += 1) {
      await new Promise((r) => setTimeout(r, 1500));
      try {
        const res = await axios.get(`/api/scheduled-emails/${scheduledId}`);
        const status = res.data?.email?.status;
        const err = res.data?.email?.error || null;
        if (err) lastError = err;
        if (status === 'sent') {
          finalStatus = 'sent';
          break;
        }
        if (status === 'failed' || status === 'cancelled') {
          finalStatus = status;
          lastError = err || lastError;
          break;
        }
      } catch (_) {
        /* retry */
      }
    }
    if (finalStatus === 'sent') {
      toast.success('E-Mail erfolgreich gesendet!', { id: 'scheduled-send' });
      try {
        if (draftId) await removeComposerDraft(mailbox, draftId);
        else await clearComposerDraft(mailbox);
      } catch (_) {}
      window.dispatchEvent(new CustomEvent('odium:sent-refresh'));
      window.dispatchEvent(
        new CustomEvent('odium:composer-draft-cleared', {
          detail: { draftId: draftId || null },
        })
      );
    } else if (finalStatus === 'failed') {
      toast.error(
        lastError ? `Versand fehlgeschlagen: ${lastError}` : 'Versand fehlgeschlagen',
        { id: 'scheduled-send' }
      );
    } else if (finalStatus === 'cancelled') {
      toast('Versand abgebrochen', { id: 'scheduled-send', icon: '↩️' });
    } else {
      // Noch pending/sending nach Polling — Fehler aus Queue anzeigen, falls vorhanden
      toast.error(
        lastError
          ? `Versand noch nicht bestätigt: ${lastError}. Bitte unter Gesendet prüfen oder erneut senden.`
          : 'Versand noch nicht bestätigt. Bitte unter Gesendet prüfen oder erneut senden.',
        { id: 'scheduled-send' }
      );
    }
  }, []);

  const startPending = useCallback(
    ({ scheduledId, cancelToken, to, subject, delaySeconds, draftId = null, mailbox = null }) => {
      clearTimers();
      const delay = Math.max(1, Math.min(60, Number(delaySeconds) || 10));
      const endsAt = Date.now() + delay * 1000;
      const next = {
        scheduledId,
        cancelToken: cancelToken || null,
        to: String(to || '').trim(),
        subject: String(subject || '').trim(),
        endsAt,
        delaySeconds: delay,
        draftId: draftId || null,
        mailbox: mailbox || null,
      };
      pendingRef.current = next;
      setPending(next);
      setSecondsLeft(delay);

      tickRef.current = window.setInterval(() => {
        const left = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));
        setSecondsLeft(left);
        if (left <= 0 && tickRef.current) {
          window.clearInterval(tickRef.current);
          tickRef.current = null;
        }
      }, 200);

      expireRef.current = window.setTimeout(() => {
        const current = pendingRef.current;
        finishPending(null);
        if (current?.scheduledId) {
          watchScheduledCompletion(current.scheduledId, {
            draftId: current.draftId,
            mailbox: current.mailbox,
          });
        }
      }, delay * 1000);
    },
    [clearTimers, finishPending, watchScheduledCompletion]
  );

  const requestResume = useCallback(() => {
    setResumeNonce((n) => n + 1);
  }, []);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const value = useMemo(
    () => ({
      pending,
      secondsLeft,
      isPending: !!pending && secondsLeft != null && secondsLeft > 0,
      resumeNonce,
      startPending,
      cancelPending,
      requestResume,
    }),
    [pending, secondsLeft, resumeNonce, startPending, cancelPending, requestResume]
  );

  return (
    <PendingSendContext.Provider value={value}>{children}</PendingSendContext.Provider>
  );
};

export function PendingSendBanner() {
  const navigate = useNavigate();
  const { pending, secondsLeft, isPending, cancelPending, requestResume } = usePendingSend();

  if (!isPending || !pending) return null;

  const openComposer = () => {
    requestResume();
    navigate('/inbox');
  };

  return (
    <div className="fixed bottom-4 left-1/2 z-[80] w-[min(36rem,calc(100%-1.5rem))] -translate-x-1/2">
      <div className="flex items-center gap-3 rounded-2xl border border-amber-300/70 bg-amber-50/95 px-3.5 py-3 shadow-lg shadow-amber-900/10 backdrop-blur dark:border-amber-500/40 dark:bg-amber-950/95">
        <button
          type="button"
          onClick={openComposer}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
          title="Zurück zur E-Mail"
        >
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-amber-500 text-white">
            <Mail className="h-5 w-5" />
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-amber-950 dark:text-amber-50">
              Versand in {secondsLeft}s
            </span>
            <span className="block truncate text-xs text-amber-800/90 dark:text-amber-200/90">
              {pending.to || 'Empfänger'}
              {pending.subject ? ` · ${pending.subject}` : ''}
            </span>
            <span className="mt-0.5 block text-[11px] font-medium text-amber-700 dark:text-amber-300">
              Tippen zum Öffnen der E-Mail
            </span>
          </span>
        </button>
        <button
          type="button"
          onClick={() => cancelPending()}
          className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-xl bg-amber-500 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-600"
          title="Versand rückgängig machen"
        >
          <Undo2 className="h-3.5 w-3.5" />
          Stop
        </button>
      </div>
    </div>
  );
}
